import { type MarketDataCardConfig, UnifiedLiquidityEnum } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useMemo } from 'react';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { useMarketDataMarketSelectorItems } from '../MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { useMarketSelectorAggregationPresets } from '../MarketSelector/presets/useMarketSelectorAggregationPresets';
import { getSelectionsFromPreset } from '../MarketSelector/useMarketSelector';

/**
 * This hook handles forward-backward compat while we juggle new and old market selector functionality.
 * The intention is to clean up the market data card state when we remove the old market selector entirely,
 * which would envelop the behavior below effectively as well.
 * @param config the config coming from the AppConfig
 * @param save Function to update the app config. The hook will call this whenever it needs to "correct" the AppConfig state.
 * @returns hydrated app config for you to use
 */
export const useHydratedMarketDataCardConfig = (
  config: MarketDataCardConfig,
  save: (config: MarketDataCardConfig) => void
) => {
  const { useNewMarketSelector } = useTradingSettings();
  const { aggregationPresets } = useMarketSelectorAggregationPresets();

  const items = useMarketDataMarketSelectorItems({
    symbol: config.symbol,
    includeUnifiedLiquidityMarkets: config.unifiedLiquidity === UnifiedLiquidityEnum.Enabled,
    queryMSSRecords: false, // we dont ask for mss records in this implementation, they aren't needed for our hydration purposes
  });

  const itemsMap = useMemo(() => new Map(items.map(item => [item.name, item])), [items]);

  const hydratedConfig = useMemo(() => {
    if (!useNewMarketSelector || !config.selectedAggregation) {
      // Either we're using the old mkt selector, or there's no aggregation selected. We do not need to hydrate.
      return config;
    }

    if (!aggregationPresets) {
      // wait for the presets to load before doing anything. while waiting we just return the old config.
      return config;
    }

    // and down here set the markets and accounts of the config to be that of the latest state of the aggregation.
    const agg = aggregationPresets.find(agg => agg.id === config.selectedAggregation);
    if (!agg) {
      // aggregation no longer exists. remove the selected aggregation. keep the accounts that were selected thru that aggregation.
      const newConfig: MarketDataCardConfig = {
        ...config,
        selectedAggregation: undefined,
      };

      save(newConfig);
      return newConfig;
    }

    const hydratedSelections = getSelectionsFromPreset(agg, itemsMap).map(item => item.name);

    if (isEqual(config.selectedMarketAccounts, hydratedSelections)) {
      // no change - no hydration necessary
      return config;
    }

    const newConfig: MarketDataCardConfig = {
      ...config,
      selectedMarketAccounts: hydratedSelections,
      availableMarketAccounts: hydratedSelections, // <-- for some reason we do this in some places. Just sticking to the pattern here, but looks useless to me.
    };

    save(newConfig);
    return newConfig;
  }, [config, useNewMarketSelector, aggregationPresets, save, itemsMap]);

  return hydratedConfig;
};
